import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "convex/_generated/api";
import { useNavigate } from "@tanstack/react-router";
import {
  Inbox,
  ClipboardCheck,
  FileText,
  FileBarChart,
  FileCheck,
  FlaskConical,
  ShieldCheck,
  LayoutDashboard,
} from "lucide-react";
import { useTranslation } from "@/packages/utils";
import type {
  SidebarProps,
  RecentAudit,
  TeamMember,
} from "@/components/sidebar";

const AVATAR_COLORS = [
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#EC4899",
  "#06B6D4",
  "#F97316",
];

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return Math.abs(hash);
}

function getAvatarColor(name: string): string {
  return AVATAR_COLORS[hashString(name) % AVATAR_COLORS.length];
}

interface UseSidebarDataReturn {
  sidebarProps: SidebarProps;
  isLoading: boolean;
}

export function useSidebarData(workspaceSlug: string): UseSidebarDataReturn {
  const { t } = useTranslation(["sidebar"]);
  const navigate = useNavigate();
  const prefix = workspaceSlug ? `/${workspaceSlug}` : "";

  const handleCreateWorkspace = useCallback(() => {
    navigate({ to: "/onboarding", search: { new: true } });
  }, [navigate]);

  const { data: currentUserData, isLoading: currentUserLoading } = useQuery(
    convexQuery(api.users.getCurrent, {}),
  );

  const { data: workspacesData } = useQuery(
    convexQuery(api.workspaces.getMyWorkspaces, {}),
  );
  const currentWorkspace = workspacesData?.find(
    (ws) => ws.slug === workspaceSlug,
  );

  const { data: audits, isLoading: auditsLoading } = useQuery(
    convexQuery(
      api.audit_queries.listAudits,
      currentWorkspace?._id ? { workspaceId: currentWorkspace._id } : "skip",
    ),
  );

  const { data: workspaceMembers, isLoading: workspaceMembersLoading } =
    useQuery(
      convexQuery(
        api.workspaces.getMembers,
        currentWorkspace?._id ? { workspaceId: currentWorkspace._id } : "skip",
      ),
    );

  const { data: unreadData } = useQuery(
    convexQuery(api.notifications.queries.index.unreadCount, {}),
  );

  const recentAudits: RecentAudit[] = [...(audits ?? [])]
    .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))
    .slice(0, 5)
    .map((audit) => ({
      id: audit.id,
      name: audit.displayName,
      icon: FileCheck,
      iconColor: "#3B82F6",
      to: `${prefix}/audits/${audit.id}`,
    }));

  const teamMembers: TeamMember[] = (workspaceMembers ?? []).map((member) => ({
    name: member.userName,
    avatarColor: member.avatarColor,
  }));

  const userName = currentUserData?.name ?? currentUserData?.email ?? "User";
  const currentUser = currentUserData
    ? {
        name: userName,
        email: currentUserData.email ?? "",
        avatarUrl: (currentUserData as Record<string, unknown>).image as
          | string
          | undefined,
        avatarColor: getAvatarColor(userName),
      }
    : undefined;

  const workspaceInfo = currentWorkspace
    ? {
        name: currentWorkspace.name ?? "Unknown Workspace",
        initials: currentWorkspace.name?.slice(0, 2).toUpperCase() ?? "??",
        color:
          currentWorkspace.accentColor ??
          getAvatarColor(currentWorkspace.name ?? "Unknown Workspace"),
        currentSlug: workspaceSlug,
        onNew: handleCreateWorkspace,
      }
    : {
        name: "...",
        initials: "..",
        color: "var(--color-accent)",
        currentSlug: workspaceSlug,
        onNew: handleCreateWorkspace,
      };

  const sidebarProps: SidebarProps = {
    workspace: workspaceInfo,
    primaryNav: [
      {
        icon: LayoutDashboard,
        label: t("sidebar:nav.dashboard"),
        to: `${prefix}/dashboard`,
      },
      {
        icon: Inbox,
        label: t("sidebar:nav.inbox"),
        to: `${prefix}/inbox`,
        badge: unreadData?.count ? unreadData.count : undefined,
      },
      {
        icon: ClipboardCheck,
        label: t("sidebar:nav.audits"),
        to: `${prefix}/audits`,
      },
      {
        icon: FileText,
        label: t("sidebar:nav.documents"),
        to: `${prefix}/documents`,
      },
      {
        icon: FileBarChart,
        label: t("sidebar:nav.reports"),
        to: `${prefix}/reports`,
      },
      ...(import.meta.env.DEV
        ? [
            {
              icon: FlaskConical,
              label: t("sidebar:nav.playground"),
              to: "/dev",
            },
          ]
        : []),
    ],
    recentAudits,
    teamMembers,
    bottomNav: import.meta.env.DEV
      ? [
          {
            icon: ShieldCheck,
            label: t("sidebar:nav.standards"),
            to: `${prefix}/admin/standards`,
          },
        ]
      : [],
    currentUser,
  };

  return {
    sidebarProps,
    isLoading: auditsLoading || workspaceMembersLoading || currentUserLoading,
  };
}
